import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
} from '@material-ui/core';
import MapIcon from '@material-ui/icons/Map';
import { useTranslate } from 'react-admin';
import DepartmentMapPicker from './DepartmentMapPicker';
import { departmentLabel, formatDepartment } from './departments';

const SUMMARY_MAX_LABELS = 4;

interface DepartmentFilterFieldProps {
    value: number[];
    onChange: (codes: number[]) => void;
}

/** "Départements" filter of the ANC list and map: click the departments to see on a map of France. */
const DepartmentFilterField: React.FC<DepartmentFilterFieldProps> = ({ value, onChange }) => {
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<number[]>(value);

    const labels = value.slice(0, SUMMARY_MAX_LABELS).map(departmentLabel).join(', ');
    const more = value.length > SUMMARY_MAX_LABELS ? ` +${value.length - SUMMARY_MAX_LABELS}` : '';

    const openDialog = () => {
        setDraft(value);
        setOpen(true);
    };

    const apply = () => {
        onChange(draft);
        setOpen(false);
    };

    return (
        <>
            <Button variant='outlined' color='primary' startIcon={<MapIcon />} onClick={openDialog}>
                {translate('app.ancs.departmentFilter.label')}
                {' : '}
                {value.length ? `${labels}${more}` : translate('app.ancs.departmentFilter.all')}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
                <DialogTitle>{translate('app.ancs.departmentFilter.title')}</DialogTitle>
                <DialogContent>
                    <DepartmentMapPicker
                        departmentCodes={draft}
                        onChange={setDraft}
                        activeLabel={translate('app.ancs.departmentFilter.selected')}
                    />
                    <FormHelperText>{translate('app.ancs.departmentFilter.hint')}</FormHelperText>
                    <Box display='flex' flexWrap='wrap' style={{ gap: 8, marginTop: 8 }}>
                        {draft.map((code) => (
                            <Chip
                                key={code}
                                size='small'
                                label={formatDepartment(code)}
                                onDelete={() => setDraft(draft.filter((item) => item !== code))}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDraft([])} disabled={draft.length === 0}>
                        {translate('app.ancs.departmentFilter.clear')}
                    </Button>
                    <Button onClick={() => setOpen(false)}>{translate('app.ancs.departmentFilter.cancel')}</Button>
                    <Button color='primary' variant='contained' onClick={apply}>
                        {translate('app.ancs.departmentFilter.apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DepartmentFilterField;
